import React from 'react';
import moment, { isMoment, Moment } from 'moment';
import { useController, useFormContext } from 'react-hook-form';
import DatePickerReact, { registerLocale } from 'react-datepicker';
import { cs, sk, es, enGB, hu, uk, ru, de, fr } from 'date-fns/locale';
import { useAppContext } from '../../../../Services/AppContext';
import { joinClasses } from '../../../../Utils/className.util';
import TopFieldContent from '../TopFieldContent';
import ErrorMessage from '../ErrorMessage';
import useTranslation from '../../../../Hooks/useTranslation';
import useFormName from '../../../../Hooks/useFormName';
// @ts-ignore
import styles from './DateField.module.scss';
import 'react-datepicker/dist/react-datepicker.css';

const languages = {
	cs: cs,
	sk: sk,
	hu: hu,
	en: enGB,
	ru: ru,
	uk: uk,
	es: es,
	de: de,
	fr: fr,
};
for (const language in languages) {
	registerLocale(language, languages[language]);
}

interface IDatePicker {
	onChange?: any;
	value?: Moment;
	placeholder?: string;
	required?: boolean;
	usePortal?: boolean;
	minDate?: Moment;
	maxDate?: Moment;
	name: string;
	label?: string;
	className?: string;
	isRequired?: boolean;
}

const DateField = ({
	name,
	label,
	usePortal,
	placeholder,
	minDate,
	maxDate,
	className,
	isRequired,
}: IDatePicker) => {
	const { language } = useAppContext();
	const { t } = useTranslation();

	const { fieldName } = useFormName({ name });
	const { control } = useFormContext();

	const {
		field,
		fieldState: { error },
	} = useController({
		control,
		name: fieldName,
		defaultValue: '',
	});

	const handleOnChange = (e: any) => {
		field.onChange(moment(e).toISOString());
	};

	return (
		<div className={className}>
			{label && <TopFieldContent label={label} isRequired={isRequired} />}
			<DatePickerReact
				minDate={minDate?.toDate()}
				maxDate={maxDate?.toDate()}
				placeholderText={placeholder}
				portalId={usePortal ? 'datepicker-portal' : undefined}
				value={
					moment(field.value).isValid()
						? moment(field.value).format('DD.MM.yyyy')
						: ''
				}
				selected={
					field.value && isMoment(field.value)
						? field.value.toDate()
						: undefined
				}
				dateFormat={t('base.dateFormatJsReact')}
				locale={language}
				onChange={handleOnChange}
				className={joinClasses([
					'form-control',
					styles.input,
					Boolean(error?.message) ? styles.error : undefined,
				])}
			/>
			{error?.message && <ErrorMessage tx={error.message} />}
		</div>
	);
};

export default DateField;
